import React, { useEffect, useState, useCallback } from 'react';
import { Table, Switch, Button, Modal, Form, Input, Select, message } from 'antd';
import { adminService } from '../../services/adminService';
import type { ColumnsType } from 'antd/es/table';
import { useUserContext } from '../../context/UserContext';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';

interface User {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
  group_id: number;
  group_name: string;
  created_on: string;
  updated_on: string;
  created_by_name: string;
  updated_by_name: string;
}

interface Group {
  id: number;
  name: string;
}

const UsersAdminTab: React.FC = () => {
  const { userContext } = useUserContext();
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();

  const fetchGroups = useCallback(async () => {
    try {
      const data = await adminService.getActiveGroups();
      setGroups(data);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch groups');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!JSON.parse(localStorage.getItem('user_details') as string).features.includes('FE_ADMIN')) {
      return;
    }

    setLoading(true);
    try {
      const data = await adminService.getAllUsers();
      setUsers(data);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [userContext]);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, [fetchUsers, fetchGroups]);

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      is_active: user.is_active,
      group_id: user.group_id,
    });
    setEditModalVisible(true);
  };

  const handleSave = async (values: any) => {
    if (!editingUser) return;

    try {
      await adminService.editUser({
        id: editingUser.id,
        ...values,
      });
      message.success('User updated successfully');
      setEditModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user');
    }
  };

  const getFilteredData = () => {
    if (!searchText) return users;

    return users.filter(user =>
      user.name.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      user.group_name.toLowerCase().includes(searchText.toLowerCase()) ||
      new Date(user.created_on).toLocaleDateString().includes(searchText)
    );
  };

  const handleCreate = async (values: any) => {
    try {
      await adminService.addUser({
        name: values.name,
        email: values.email,
        is_active: values.is_active ?? true,
        group_id: values.group_id,
      });
      message.success('User created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchUsers();
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to create user';
      if (errorMsg.toLowerCase().includes('already exists')) {
        message.warning(errorMsg + '. Showing matching users below.');
        setCreateModalVisible(false);
        createForm.resetFields();
        setSearchText(values.email);
      } else {
        message.error(errorMsg);
      }
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'group_name',
      key: 'group_name',
      filters: groups.map(group => ({ text: group.name, value: group.name })),
      onFilter: (value, record) => record.group_name === value,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <span style={{ color: active ? 'green' : 'red' }}>
          {active ? 'Active' : 'Inactive'}
        </span>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Created On',
      dataIndex: 'created_on',
      key: 'created_on',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_on).getTime() - new Date(b.created_on).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="primary" onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search users..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
        >
          Create User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={getFilteredData()}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title="Create New User"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createForm} onFinish={handleCreate} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' },
              {
                validator: (_, value) =>
                  !value || value.toLowerCase().endsWith('@blend360.com')
                    ? Promise.resolve()
                    : Promise.reject('Only @blend360.com email addresses are allowed'),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="group_id"
            label="Role"
            rules={[{ required: true, message: 'Please select a role!' }]}
          >
            <Select>
              {groups.map(group => (
                <Select.Option key={group.id} value={group.id}>
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            layout="horizontal"
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Create User
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleSave} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' },
              {
                validator: (_, value) =>
                  !value || value.toLowerCase().endsWith('@blend360.com')
                    ? Promise.resolve()
                    : Promise.reject('Only @blend360.com email addresses are allowed'),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="group_id"
            label="Role"
            rules={[{ required: true, message: 'Please select a role!' }]}
          >
            <Select>
              {groups.map(group => (
                <Select.Option key={group.id} value={group.id}>
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            layout="horizontal"
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersAdminTab;
